import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  Check,
  CheckCircle,
  Clock,
  Eye,
  History,
  KeyRound,
  Loader2,
  Lock,
  RefreshCw,
  Search,
  Settings,
  ShieldCheck,
  UserCheck,
  X,
  XCircle
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { Toast } from './ui/Toast';
import { LongToast, shouldUseLongToast } from './ui/LongToast';
import { api } from '../services/api';
import CredentialsModal from './CredentialsModal';
import DetailsDemandeStructuree, { aUnDescripteur } from './support/DetailsDemandeStructuree';
import { useFeatures } from '../contexts/FeaturesContext';
import { usePermissions } from '../hooks/usePermissions';
import { usePermissionsAnnulationPrivilegiee } from './avances/annulationPrivilegieeApi';

// Lien direct vers une demande spéciale (13/10/2026) :
//   /administration/support?tab=special&demande=<id>
// Posé par le panneau des alertes critiques (corrections en attente). Le
// paramètre est lu une fois puis retiré de l'adresse (replace) : un
// rechargement ou un retour arrière ne rouvre pas la fenêtre.
const PARAM_DEMANDE = 'demande';

// La file affichée ne couvre que l'année choisie et les 50 dernières demandes ;
// un lien peut viser une demande d'une autre année ou plus ancienne. On la
// cherche alors toutes années confondues, par pages de 100, sans dépasser
// cette borne (49 demandes au plus par école, relevé du 13/10/2026).
const PAGES_MAX_RECHERCHE = 10;

// Une correction qui fait payer davantage à la famille ne s'approuve pas par
// la personne qui l'a demandée (décision de Daniel du 16/10/2026, après la
// demande n°53 de csap1). La règle est au serveur (SpecialValidationService,
// `faitPayerDavantage`), qui la renvoie dans `fait_payer_davantage` : l'écran
// la lit, il ne la refait pas. Même phrase des deux côtés, écrite en clair.
const CODE_AUTO_VALIDATION_HAUSSE = 'AUTO_VALIDATION_HAUSSE';
const PHRASE_AUTO_VALIDATION_HAUSSE = 'Cette correction fait payer davantage à la famille : '
  + "elle doit être validée par une autre personne que celle qui l'a demandée.";

// Seule exception (GO de Daniel du 17/10/2026) : le super validateur
// (dérogation COMPTABILITE_ET_PAIE.ACTIONS_PRIVILEGIEES.SUPER_VALIDER, jamais
// par rôle, groupe ni ALL_MODULES) peut approuver sa propre correction en
// hausse, aux conditions de toute auto-validation, que le serveur vérifie
// (utils/autoValidationPrivilegiee.js). L'écran les écrit en clair à côté du
// bouton, jamais dans une infobulle.
const LONGUEUR_JUSTIFICATION_AUTO = 30;
const MENTION_SUPER_VALIDATEUR_HAUSSE = 'Vous avez demandé cette correction, qui fait payer davantage à la famille. '
  + "Comme super validateur, vous pouvez l'approuver vous-même : la justification de la demande doit compter "
  + `${LONGUEUR_JUSTIFICATION_AUTO} caractères au moins, et votre code de double authentification vous sera demandé. `
  + "L'approbation sera marquée « auto-validée » au journal des actions privilégiées et signalée à la direction.";
// Les seuls types où le serveur laisse passer le super validateur
// (SpecialValidationService, TYPES_AUTO_VALIDATION_HAUSSE) : sur un autre type
// portant l'avertissement, lui aussi est refusé, l'écran ne promet rien.
const TYPES_AUTO_VALIDATION_HAUSSE = [
  'modification_reduction_eleve',
  'creation_famille_depuis_eleves_seuls',
  'ajout_eleve_famille_nombreuse'
];

const idAgentConnecte = () => {
  try {
    return Number(JSON.parse(localStorage.getItem('user') || '{}')?.id) || null;
  } catch {
    return null;
  }
};

const tabFromQuery = (value) => {
  if (value === 'special') return 'special_requests';
  if (value === 'history') return 'history';
  return 'access_requests';
};

const queryFromTab = (tab) => {
  if (tab === 'special_requests') return 'special';
  if (tab === 'history') return 'history';
  return 'access';
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatMoney = (value) => `${Math.round(Number(value || 0)).toLocaleString('fr-FR')} FCFA`;

const getName = (...parts) => parts.filter(Boolean).join(' ').trim() || '-';

const normalizeStatus = (status) => {
  if (status === 'approuvee') return 'Approuvée';
  if (status === 'refusee') return 'Refusée';
  if (status === 'annulee') return 'Annulée';
  if (status === 'traitee') return 'Traitée';
  return 'En attente';
};

const statusClasses = (status) => {
  if (['approuvee', 'traitee'].includes(status)) {
    return 'bg-green-50 text-green-700 border-green-200';
  }
  if (status === 'refusee') {
    return 'bg-red-50 text-red-700 border-red-200';
  }
  if (status === 'annulee') {
    return 'bg-gray-100 text-gray-600 border-gray-200';
  }
  return 'bg-amber-50 text-amber-700 border-amber-200';
};

const typeLabel = (type) => {
  if (type === 'creation_famille_depuis_eleves_seuls') return 'Création famille nombreuse';
  if (type === 'modification_infos_famille_nombreuse') return 'Modification famille nombreuse';
  if (type === 'retrait_eleve_famille_nombreuse') return 'Retrait élève famille';
  if (type === 'ajout_eleve_famille_nombreuse') return 'Ajout élève famille';
  if (type === 'modification_reduction_eleve') return 'Modification réduction élève';
  if (type === 'modification_frais_inscription') return "Modification frais de début d'année";
  if (type === 'changement_classe_eleve') return 'Changement de classe';
  if (type === 'rattachement_eleves_famille_intersite') return 'Rattachement famille inter-sites';
  if (type === 'desactivation_famille_intersite') return 'Sortie du groupe inter-sites';
  if (type === 'modification_salaire_employe') return 'Modification salaire employé';
  if (type === 'modification_taux_horaire_classe') return "Taux horaire d'une classe";
  if (type === 'modification_infos_employe') return 'Modification informations employé';
  if (type === 'radiation_employe') return 'Radiation employé';
  if (type === 'ouverture_fenetre_paie') return 'Ouverture exceptionnelle du calendrier de paie';
  if (type === 'annulation_privilegiee_avance') return "Annulation privilégiée d'une avance";
  if (type === 'ouverture_exceptionnelle_caisse') return 'Ouverture exceptionnelle de caisse';
  if (type === 'annulation_validation_centrale_caisse') return 'Annulation validation centrale + réouverture';
  if (type === 'creation') return 'Création de compte';
  if (type === 'reinitialisation') return 'Réinitialisation';
  if (type === 'famille_reduction') return 'Réduction famille';
  if (type === 'famille_creation') return 'Création famille';
  if (type === 'reduction_sensible') return 'Réduction sensible';
  return String(type || 'Demande spéciale').replace(/_/g, ' ');
};

const formatPayloadValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  if (typeof value === 'number') return value.toLocaleString('fr-FR');
  if (Array.isArray(value)) return `${value.length} élément(s)`;
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

const renderPayloadFieldValue = (value) => {
  if (Array.isArray(value)) {
    if (value.length === 0) return <span>-</span>;

    if (value.every((item) => item && typeof item === 'object' && !Array.isArray(item))) {
      return (
        <div className="space-y-2">
          {value.slice(0, 8).map((item, index) => (
            <div key={index} className="rounded-md border border-gray-200 bg-white p-2 text-xs font-normal text-gray-700">
              {Object.entries(item).map(([itemKey, itemValue]) => (
                <div key={itemKey} className="flex justify-between gap-3 py-0.5">
                  <span className="text-gray-500">{itemKey.replace(/_/g, ' ')}</span>
                  <span className="text-right font-semibold text-gray-800">{formatPayloadValue(itemValue)}</span>
                </div>
              ))}
            </div>
          ))}
          {value.length > 8 && (
            <div className="text-xs font-semibold text-gray-500">
              + {value.length - 8} autre(s) ligne(s)
            </div>
          )}
        </div>
      );
    }
  }

  if (value && typeof value === 'object') {
    return (
      <pre className="whitespace-pre-wrap break-words rounded-md bg-white p-2 text-xs font-normal text-gray-700">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }

  return <span>{formatPayloadValue(value)}</span>;
};

const renderPayload = (payload) => {
  if (!payload) {
    return <p className="text-sm text-gray-500">Aucune donnée détaillée.</p>;
  }

  if (Array.isArray(payload)) {
    return renderPayloadFieldValue(payload);
  }

  if (typeof payload !== 'object') {
    return (
      <pre className="whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-xs text-gray-700">
        {formatPayloadValue(payload)}
      </pre>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
      {Object.entries(payload).map(([key, value]) => (
        <div key={key} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
            {key.replace(/_/g, ' ')}
          </div>
          <div className="mt-1 whitespace-pre-wrap break-words text-sm font-semibold text-gray-800">
            {renderPayloadFieldValue(value)}
          </div>
        </div>
      ))}
    </div>
  );
};

const renderSalaryModificationDetails = (request) => {
  const before = request?.payload_avant || {};
  const asked = request?.payload_demande || {};
  const employee = before.employe || {};
  const lockedMonths = Array.isArray(before.mois_verrouilles) ? before.mois_verrouilles : [];
  const employeeName = getName(employee.prenom, employee.nom);

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-blue-100 bg-blue-50 p-4">
        <div className="text-xs font-bold uppercase tracking-wide text-[#246990]">Lecture de la demande</div>
        <p className="mt-2 text-sm leading-6 text-gray-700">
          Cette demande modifie uniquement le salaire fixe mensuel a partir du mois d'effet valide.
          Les mois deja payes, clotures ou avec avance restent verrouilles.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="text-xs font-bold uppercase tracking-wide text-gray-500">Employé</div>
          <div className="mt-2 text-base font-bold text-gray-900">{employeeName}</div>
          <div className="mt-1 text-xs text-gray-500">{employee.role || employee.groupe || '-'}</div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="text-xs font-bold uppercase tracking-wide text-gray-500">Salaire actuel</div>
          <div className="mt-2 text-xl font-bold text-gray-900">{formatMoney(asked.ancien_salaire)}</div>
        </div>
        <div className="rounded-lg border border-green-200 bg-green-50 p-4">
          <div className="text-xs font-bold uppercase tracking-wide text-green-700">Nouveau salaire</div>
          <div className="mt-2 text-xl font-bold text-green-800">{formatMoney(asked.nouveau_salaire)}</div>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="text-xs font-bold uppercase tracking-wide text-amber-700">Mois d'effet</div>
          <div className="mt-2 text-xl font-bold text-amber-800">{asked.mois_effet_nom || '-'}</div>
          <div className="mt-1 text-xs text-amber-700">{asked.mois_effet_annee || ''}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="text-xs font-bold uppercase tracking-wide text-gray-500">Mois verrouillés</div>
          {lockedMonths.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {lockedMonths.map((month) => (
                <span key={month.id || `${month.nom}-${month.ordre}`} className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs font-semibold text-gray-600">
                  {month.nom}
                </span>
              ))}
            </div>
          ) : (
            <p className="mt-2 text-sm text-gray-600">Aucun mois verrouillé avant le mois d'effet.</p>
          )}
        </div>

        <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
          <div className="text-xs font-bold uppercase tracking-wide text-orange-700">Motif</div>
          <p className="mt-2 text-sm leading-6 text-orange-900">{asked.motif || request?.motif || '-'}</p>
        </div>
      </div>
    </div>
  );
};

const renderEmployeeProfileModificationDetails = (request) => {
  const before = request?.payload_avant || {};
  const asked = request?.payload_demande || {};
  const employee = before.employe || {};
  const changes = asked.champs && typeof asked.champs === 'object' ? asked.champs : {};
  const rows = Object.entries(changes);
  const justificatifs = Array.isArray(asked.justificatifs) ? asked.justificatifs : [];
  const employeeName = getName(employee.prenom, employee.nom);

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-blue-100 bg-blue-50 p-4">
        <div className="text-xs font-bold uppercase tracking-wide text-[#246990]">Lecture de la demande</div>
        <p className="mt-2 text-sm leading-6 text-gray-700">
          Cette demande mettra a jour uniquement les champs listes ci-dessous apres approbation.
          Les autres informations RH, les roles et les acces ne seront pas modifies.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="text-xs font-bold uppercase tracking-wide text-gray-500">Employé</div>
          <div className="mt-2 text-base font-bold text-gray-900">{employeeName}</div>
          <div className="mt-1 text-xs text-gray-500">{employee.role || employee.groupe || '-'}</div>
        </div>
        <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 md:col-span-2">
          <div className="text-xs font-bold uppercase tracking-wide text-orange-700">Motif</div>
          <p className="mt-2 text-sm leading-6 text-orange-900">{asked.motif || request?.motif || '-'}</p>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
            <tr>
              <th className="px-4 py-3">Champ</th>
              <th className="px-4 py-3">Valeur actuelle</th>
              <th className="px-4 py-3">Valeur demandée</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length > 0 ? rows.map(([field, change]) => (
              <tr key={field}>
                <td className="px-4 py-3 font-semibold text-gray-800">{change?.label || field.replace(/_/g, ' ')}</td>
                <td className="px-4 py-3 text-gray-600">{formatPayloadValue(change?.ancienne_valeur)}</td>
                <td className="px-4 py-3 font-semibold text-[#246990]">{formatPayloadValue(change?.nouvelle_valeur)}</td>
              </tr>
            )) : (
              <tr>
                <td colSpan={3} className="px-4 py-5 text-center text-gray-500">Aucun champ texte à modifier.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {justificatifs.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="text-xs font-bold uppercase tracking-wide text-gray-500">Justificatifs</div>
          <div className="mt-3 space-y-2">
            {justificatifs.map((item, index) => (
              <div key={`${item.type || 'fichier'}-${index}`} className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm">
                <span className="truncate font-semibold text-gray-800">{item.original_name || item.nom_fichier || 'Piece jointe'}</span>
                <span className="flex-shrink-0 text-xs font-medium text-gray-500">{item.mimetype || item.type || '-'}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
const EmptyState = ({ icon: Icon, title, text }) => (
  <div className="flex min-h-[220px] flex-col items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50/70 p-8 text-center">
    <Icon size={34} className="text-gray-300" />
    <h3 className="mt-3 text-base font-semibold text-gray-800">{title}</h3>
    <p className="mt-1 max-w-md text-sm text-gray-500">{text}</p>
  </div>
);

const StatCard = ({ title, value, subtitle, icon: Icon, color }) => (
  <div className={`rounded-lg p-5 text-white shadow-sm ${color}`}>
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-sm font-semibold text-white/90">{title}</p>
        <p className="mt-3 text-3xl font-bold">{value}</p>
        {subtitle && <p className="mt-2 text-xs text-white/80">{subtitle}</p>}
      </div>
      <div className="rounded-lg bg-white/15 p-3">
        <Icon size={22} />
      </div>
    </div>
  </div>
);

const TabButton = ({ active, icon: Icon, label, count, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold transition-colors ${active
      ? 'bg-[#246990] text-white shadow-sm'
      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
  >
    <Icon size={16} />
    <span>{label}</span>
    {Number.isFinite(count) && (
      <span className={`rounded-full px-2 py-0.5 text-xs ${active ? 'bg-white/20 text-white' : 'bg-white text-gray-600'}`}>
        {count}
      </span>
    )}
  </button>
);

// Les deux boutons d'un pied de modal ont la meme taille ET la meme largeur.
// DESIGN-SYSTEM.md, section Boutons.
const BOUTON_PIED = 'inline-flex h-9 min-w-[168px] items-center justify-center gap-2 rounded-lg border border-transparent px-4 text-sm font-semibold transition-all disabled:opacity-60';

// Coquille montee sur les primitives `Dialog` (CLAUDE.md l'impose) et non plus
// sur un `fixed inset-0` maison : c'est ce qui rend la touche Echap, le clic
// sur le fond, le piege de focus et le role ARIA. La signature n'a pas bouge,
// les quatre appels sont inchanges.
// Le temps qu'une fenêtre Radix se démonte et rende la page cliquable avant
// que la suivante ne bouge. Voir handleProcessAccessRequest.
const DELAI_FERMETURE = 60;
const attendre = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const ModalShell = ({ title, description, icon: Icone = ShieldCheck, children, onClose, footer }) => (
  <Dialog open onOpenChange={(ouvert) => { if (!ouvert) onClose(); }}>
    <DialogContent className="w-[95vw] max-w-5xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
      <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
          <Icone size={18} />
        </div>
        <div className="min-w-0">
          <DialogTitle className="text-lg font-semibold text-gray-900">{title}</DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            {description || 'Centre de support'}
          </DialogDescription>
        </div>
      </DialogHeader>
      <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
      {footer && <DialogFooter className="border-t bg-gray-50 px-5 py-4">{footer}</DialogFooter>}
    </DialogContent>
  </Dialog>
);

const Support = () => {
  const { requestId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  // Le Centre de Support porte deux files independantes :
  //   * les demandes d'acces (creation de compte, reinitialisation) : seul
  //     chemin pour donner un acces, indispensable a toute licence ;
  //   * les demandes speciales : le circuit de validation, qui n'existe que
  //     parce que ces operations ont un effet financier.
  // Sans comptabilite, la seconde n'a plus rien a valider : on la retire, elle
  // et le groupe validateur qui ne route plus rien.
  const { estActive } = useFeatures();
  const circuitValidation = estActive('module.comptabilite');

  // L'écran s'ouvre par la porte ADMINISTRATION (App.js), mais ses files
  // exigent ADMINISTRATION.SUPPORT.VIEW (support.routes.js). Sans ce droit,
  // chaque lecture répondait 403 et l'écran affichait « Lecture impossible -
  // ne la considérez pas comme vide » : une fausse alerte. Relevé du
  // 13/10/2026 : 18 comptes dans ce cas (porte ADMINISTRATION accordée aux
  // groupes Direction Générale ou Administration Générale, sans le droit de
  // lire le support). Ils lisent désormais pourquoi, et l'écran ne lance
  // aucune requête vouée au refus. La liste vient de getUserPermissionsV2 :
  // même résolution que le serveur, refus et ALL_MODULES compris.
  const { hasGranularPermission, loading: chargementDroits } = usePermissions();
  const peutConsulter = useMemo(
    () => hasGranularPermission('ADMINISTRATION', 'SUPPORT', 'VIEW'),
    [hasGranularPermission]
  );
  // Super validateur : la liste du serveur ne porte cette clé que par
  // dérogation nominative. L'écran s'en sert pour proposer, le serveur tranche.
  const { estSuperValidateur } = usePermissionsAnnulationPrivilegiee();

  const [activeTab, setActiveTab] = useState(() => {
    const demande = tabFromQuery(searchParams.get('tab'));
    return demande === 'special_requests' && !circuitValidation ? 'access_requests' : demande;
  });
  const [pendingRequests, setPendingRequests] = useState([]);
  const [specialRequests, setSpecialRequests] = useState([]);
  const [specialHistory, setSpecialHistory] = useState([]);
  const [historiqueRequests, setHistoriqueRequests] = useState([]);
  const [historyPagination, setHistoryPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    hasNext: false,
    hasPrev: false
  });
  const [securityGroups, setSecurityGroups] = useState([]);
  const [validatorGroup, setValidatorGroup] = useState(null);
  const [validatorGroupId, setValidatorGroupId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [selectedAccessRequest, setSelectedAccessRequest] = useState(null);
  const [selectedSpecialRequest, setSelectedSpecialRequest] = useState(null);
  const [processModal, setProcessModal] = useState(null);
  const [processNotes, setProcessNotes] = useState('');
  const [specialDecisionModal, setSpecialDecisionModal] = useState(null);
  const [specialDecisionComment, setSpecialDecisionComment] = useState('');
  // Code de double authentification : exigé quand un super validateur approuve
  // sa propre demande d'action privilégiée (le serveur le vérifie).
  const [codeDoubleAuth, setCodeDoubleAuth] = useState('');
  // Demandes dont le serveur a refusé l'approbation par leur auteur
  // (AUTO_VALIDATION_HAUSSE) : le bouton se grise aussi pour elles, même si la
  // file affichée a été lue avant la règle.
  const [approbationsRefusees, setApprobationsRefusees] = useState(() => new Set());
  const [showCredentials, setShowCredentials] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [typeDemande, setTypeDemande] = useState('creation');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  // Une file vide et une file qu'on n'a pas pu lire ne se ressemblent pas.
  // Elles s'affichaient pourtant a l'identique : chaque chargeur testait
  // `if (response.success)` sans `else`, et `api.support.*` attrape ses
  // propres erreurs pour renvoyer `{ success: false }` au lieu de lever. Un
  // 403, un 500, le reseau coupe : le meme ecran rassurant « Aucune demande
  // en attente ». Sur la file ou atterrissent les demandes de compte et de
  // reinitialisation, c'est le pire des resultats.
  const [erreurChargement, setErreurChargement] = useState(null);
  // La demande spéciale visée par un lien direct, en attente des files.
  const [demandeVisee, setDemandeVisee] = useState(null);

  useEffect(() => {
    const demande = tabFromQuery(searchParams.get('tab'));
    setActiveTab(demande === 'special_requests' && !circuitValidation ? 'access_requests' : demande);
  }, [searchParams, circuitValidation]);

  // Le lien direct est CONSOMMÉ : lu, puis retiré de l'adresse sans nouvelle
  // entrée d'historique. Il est lu à chaque changement d'adresse et pas
  // seulement au montage : depuis le panneau des alertes, on peut arriver ici
  // alors que l'écran est déjà ouvert (même route, seule la requête change).
  useEffect(() => {
    const brut = searchParams.get(PARAM_DEMANDE);
    if (brut === null) return;
    // L'onglet suit la demande : sans `tab=special` dans le lien, l'effet de
    // l'onglet ramènerait l'écran sur les demandes d'accès derrière la fenêtre.
    const suivants = new URLSearchParams(searchParams);
    suivants.delete(PARAM_DEMANDE);
    suivants.set('tab', queryFromTab('special_requests'));
    setSearchParams(suivants, { replace: true });

    const id = Number(brut);
    if (!Number.isInteger(id) || id <= 0) {
      showToast("Ce lien ne désigne aucune demande spéciale : l'adresse est incomplète.", 'error');
      return;
    }
    setDemandeVisee(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Les files ne se chargent que pour qui peut les lire. Le droit arrive avec
  // les permissions : l'effet repart quand il est connu.
  useEffect(() => {
    if (peutConsulter) loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peutConsulter]);

  // Ouvrir la demande visée une fois les files chargées (réussies ou non :
  // la recherche dira elle-même pourquoi elle échoue).
  useEffect(() => {
    if (!demandeVisee || !peutConsulter || isLoading) return;
    const id = demandeVisee;
    setDemandeVisee(null);
    ouvrirDemandeSpeciale(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [demandeVisee, peutConsulter, isLoading]);

  useEffect(() => {
    if (requestId && pendingRequests.length > 0) {
      const request = pendingRequests.find((item) => Number(item.id) === Number(requestId));
      if (request) {
        setSelectedAccessRequest(request);
        setActiveTab('access_requests');
      }
    }
  }, [requestId, pendingRequests]);

  // Filet de securite : quand plus aucune fenetre n'est ouverte, la page doit
  // etre cliquable. Si une fermeture a laisse `pointer-events: none` sur le
  // body — le blocage constate apres chaque decision —, on le retire. On ne
  // touche a rien tant qu'une fenetre, d'ici ou d'ailleurs, est encore ouverte.
  const aucuneFenetre = !selectedAccessRequest && !processModal
    && !selectedSpecialRequest && !specialDecisionModal && !showCredentials;
  useEffect(() => {
    if (!aucuneFenetre) return undefined;
    const minuteur = setTimeout(() => {
      const encoreOuverte = document.querySelector('[role="dialog"][data-state="open"]');
      if (!encoreOuverte && document.body.style.pointerEvents === 'none') {
        document.body.style.pointerEvents = '';
      }
    }, 300);
    return () => clearTimeout(minuteur);
  }, [aucuneFenetre]);

  // Le retour d'action passait par un bandeau insere dans le flux de la page,
  // pose et retire a la main par `setTimeout(…, 3500)`. CLAUDE.md : `Toast`.
  // ⚠️ UN TROISIÈME ARGUMENT `options` : sans lui, cet écran — celui où les
  // demandes de radiation et de restauration sont approuvées ou refusées —
  // n'avait aucun moyen de demander le toast long, et ses messages de refus
  // relayés du serveur disparaissaient en 3 secondes.
  const showToast = (message, type = 'success', options = {}) => setToast({ show: true, message, type, ...options });

  // Enveloppe commune : un chargement qui echoue le DIT, et le dit une fois.
  // `Promise.all` n'avait pas de `catch` non plus — la promesse rejetee
  // partait en « unhandled rejection » et l'ecran restait vide.
  const executerChargement = async (travail) => {
    try {
      await travail();
      setErreurChargement(null);
    } catch (error) {
      console.error('[Support] Chargement impossible :', error);
      setErreurChargement(error?.message || 'Lecture impossible.');
    }
  };

  const loadInitialData = async () => {
    setIsLoading(true);
    try {
      await executerChargement(() => Promise.all([
        loadAccessRequests(),
        loadHistorique(),
        ...(circuitValidation
          ? [loadSpecialRequests(), loadSpecialHistory(), loadSecurityConfig()]
          : [])
      ]));
    } finally {
      setIsLoading(false);
    }
  };

  const loadAccessRequests = async () => {
    const response = await api.support.getPendingRequests();
    if (!response?.success) {
      throw new Error(response?.error || response?.message
        || "Impossible de lire les demandes d'accès.");
    }
    setPendingRequests(Array.isArray(response.data) ? response.data : []);
  };

  const loadSpecialRequests = async () => {
    const response = await api.support.getSpecialRequests({ statut: 'en_attente', limit: 50 });
    if (!response?.success) {
      throw new Error(response?.error || response?.message
        || 'Impossible de lire les demandes spéciales.');
    }
    setSpecialRequests(Array.isArray(response.data) ? response.data : []);
  };

  const loadSpecialHistory = async () => {
    const response = await api.support.getSpecialRequests({
      statut: 'approuvee,refusee,annulee',
      limit: 50
    });
    if (!response?.success) {
      throw new Error(response?.error || response?.message
        || "Impossible de lire l'historique des demandes spéciales.");
    }
    setSpecialHistory(Array.isArray(response.data) ? response.data : []);
  };

  const loadSecurityConfig = async () => {
    const [groupsResponse, configResponse] = await Promise.all([
      api.groupesSecurite.getAll(),
      api.support.getSpecialRequestsConfig()
    ]);

    if (configResponse && configResponse.success === false) {
      throw new Error(configResponse.error || configResponse.message
        || 'Impossible de lire la configuration du groupe validateur.');
    }

    const groups = Array.isArray(groupsResponse?.data)
      ? groupsResponse.data
      : Array.isArray(groupsResponse)
        ? groupsResponse
        : [];

    setSecurityGroups(groups);

    if (configResponse?.success && configResponse.data) {
      setValidatorGroup(configResponse.data);
      setValidatorGroupId(configResponse.data.id ? String(configResponse.data.id) : '');
    }
  };

  const loadHistorique = async (page = 1) => {
    setIsLoadingHistory(true);
    try {
      const response = await api.support.getHistorique(page);
      if (!response?.success) {
        throw new Error(response?.error || response?.message
          || "Impossible de lire l'historique.");
      }
      setHistoriqueRequests(Array.isArray(response.data) ? response.data : []);
      setHistoryPagination(response.pagination || historyPagination);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Une demande spéciale par son numéro, toutes années et tous statuts
  // confondus. La route n'a pas de lecture unitaire : on parcourt la liste
  // (`all_years`), des plus récentes aux plus anciennes. Lève si la lecture
  // échoue, rend null si la demande n'existe pas.
  const chercherDemandeSpeciale = async (id) => {
    for (let page = 1; page <= PAGES_MAX_RECHERCHE; page += 1) {
      const response = await api.support.getSpecialRequests({ all_years: 1, limit: 100, page });
      if (!response?.success) {
        throw new Error(response?.error || response?.message
          || 'Impossible de lire les demandes spéciales.');
      }
      const lot = Array.isArray(response.data) ? response.data : [];
      const trouvee = lot.find((item) => Number(item.id) === id);
      if (trouvee) return trouvee;
      if (!response.pagination?.hasNext) return null;
    }
    return null;
  };

  // Ouvre le détail de la demande visée par un lien direct. D'abord dans les
  // files déjà chargées (en attente, puis historique), sinon par recherche.
  // Une demande déjà décidée s'ouvre aussi : sa fenêtre montre le statut et
  // n'offre plus de décision.
  const ouvrirDemandeSpeciale = async (id) => {
    if (!circuitValidation) {
      showToast(
        `La demande spéciale n° ${id} ne peut pas s'afficher : le circuit de validation n'est pas ouvert pour votre établissement.`,
        'error'
      );
      return;
    }

    let demande = [...specialRequests, ...specialHistory].find((item) => Number(item.id) === id) || null;
    if (!demande) {
      try {
        demande = await chercherDemandeSpeciale(id);
      } catch (error) {
        showToast(`La demande spéciale n° ${id} n'a pas pu être lue : ${error.message}`, 'error');
        return;
      }
    }

    if (!demande) {
      showToast(
        `La demande spéciale n° ${id} est introuvable : elle n'existe pas ou plus dans cet établissement.`,
        'error'
      );
      return;
    }

    setActiveTab('special_requests');
    // Une fenêtre à la fois, comme après une décision : si l'on arrive du
    // panneau des alertes, il est encore en train de se fermer.
    await attendre(DELAI_FERMETURE);
    setSelectedSpecialRequest(demande);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab: queryFromTab(tab) });
    if (tab === 'history') executerChargement(() => loadHistorique());
    if (tab === 'special_requests' && circuitValidation) {
      executerChargement(() => loadSpecialRequests());
    }
    if (tab === 'access_requests') executerChargement(() => loadAccessRequests());
  };

  const filteredAccessRequests = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term || activeTab !== 'access_requests') return pendingRequests;
    return pendingRequests.filter((request) => {
      const content = [
        request.nom,
        request.prenom,
        request.email,
        request.demandeur_nom,
        request.demandeur_prenom,
        request.type_demande
      ].filter(Boolean).join(' ').toLowerCase();
      return content.includes(term);
    });
  }, [pendingRequests, searchTerm, activeTab]);

  const filteredSpecialRequests = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term || activeTab !== 'special_requests') return specialRequests;
    return specialRequests.filter((request) => {
      const content = [
        request.titre,
        request.description,
        request.motif,
        request.type_demande,
        request.demandeur_nom,
        request.demandeur_prenom
      ].filter(Boolean).join(' ').toLowerCase();
      return content.includes(term);
    });
  }, [specialRequests, searchTerm, activeTab]);

  const stats = useMemo(() => ({
    accessPending: pendingRequests.length,
    specialPending: specialRequests.length,
    historyTotal: Number(historyPagination.total || 0) + specialHistory.length,
    validator: validatorGroup?.nom || 'Non configuré'
  }), [pendingRequests.length, specialRequests.length, historyPagination.total, specialHistory.length, validatorGroup]);

  const openProcessModal = (request, action) => {
    setProcessModal({ request, action });
    setProcessNotes('');
  };

  const handleProcessAccessRequest = async () => {
    if (!processModal?.request) return;
    if (processModal.action === 'reject' && !processNotes.trim()) {
      showToast('Le motif du refus est obligatoire.', 'error');
      return;
    }

    setIsProcessing(true);
    try {
      const response = await api.support.processRequest(
        processModal.request.id,
        processModal.action,
        { notes: processNotes.trim() }
      );

      if (response.success) {
        const action = processModal.action;
        const typeDemandeTraitee = response.typeDemande || processModal.request.type_demande;

        // ------------------------------------------------------------------
        // Les fenêtres se ferment UNE PAR UNE
        // ------------------------------------------------------------------
        // La page se figeait après chaque décision. Tout se faisait dans le même
        // rendu : la confirmation et le détail — deux fenêtres Radix empilées —
        // disparaissaient ensemble, la fenêtre des identifiants s'ouvrait, et
        // `navigate` changeait de route. Radix rend la page inerte
        // (`pointer-events: none` sur le body) tant qu'une fenêtre est ouverte ;
        // démontées ensemble, elles ne le rendaient pas.
        //
        // Pire depuis une notification : `support/requests/:id` et `support` sont
        // deux routes distinctes dans App.js, et `navigate` REMONTAIT tout le
        // composant sous les fenêtres ouvertes. L'adresse est maintenant
        // corrigée sans navigation — la demande traitée a quitté la file, rien ne
        // la rouvrira.
        setProcessModal(null);
        await attendre(DELAI_FERMETURE);
        setSelectedAccessRequest(null);
        await attendre(DELAI_FERMETURE);

        if (requestId) {
          window.history.replaceState(window.history.state, '', '/administration/support?tab=access');
        }

        if (action === 'approve' && response.credentials) {
          setCredentials(response.credentials);
          setTypeDemande(typeDemandeTraitee);
          setShowCredentials(true);
        } else {
          showToast(action === 'approve' ? 'Demande approuvée.' : 'Demande refusée.');
        }

        await Promise.all([loadAccessRequests(), loadHistorique()]);
      } else {
        showToast(response.message || response.error || 'Traitement impossible.', 'error');
      }
    } catch (error) {
      showToast(error.message || 'Erreur lors du traitement.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSaveSpecialConfig = async () => {
    if (!validatorGroupId) {
      showToast('Sélectionnez un groupe validateur.', 'error');
      return;
    }

    setIsSavingConfig(true);
    try {
      const response = await api.support.updateSpecialRequestsConfig({
        groupe_validateur_id: Number(validatorGroupId)
      });

      if (response.success) {
        const group = securityGroups.find((item) => String(item.id) === String(validatorGroupId));
        setValidatorGroup({
          id: Number(validatorGroupId),
          nom: response.data?.groupe_nom || group?.nom || 'Groupe validateur'
        });
        showToast('Groupe validateur enregistré.');
      } else {
        showToast(response.message || response.error || 'Configuration impossible.', 'error');
      }
    } finally {
      setIsSavingConfig(false);
    }
  };

  const openSpecialDecisionModal = (request, decision) => {
    setSpecialDecisionModal({ request, decision });
    setSpecialDecisionComment('');
    setCodeDoubleAuth('');
  };

  // Auto-validation d'une action privilégiée : la personne connectée approuve
  // une demande qu'elle a faite elle-même.
  const estAutoValidationPrivilegiee = (modal) => {
    if (!modal || modal.decision !== 'approve') return false;
    // Types d'actions privilégiées qu'un super validateur peut valider seul.
    if (!['annulation_privilegiee_avance', 'ouverture_fenetre_paie'].includes(modal.request?.type_demande)) return false;
    let moi = null;
    try {
      moi = JSON.parse(localStorage.getItem('user') || '{}')?.id;
    } catch {
      moi = null;
    }
    return Boolean(moi) && Number(moi) === Number(modal.request?.demandeur_id);
  };

  // L'agent connecté a lui-même demandé cette correction qui fait payer
  // davantage à la famille (drapeau calculé par le serveur).
  const estMaCorrectionEnHausse = (request) => {
    if (!request || request.statut !== 'en_attente') return false;
    if (request.fait_payer_davantage !== true) return false;
    const moi = idAgentConnecte();
    return Boolean(moi) && moi === Number(request.demandeur_id);
  };

  // « Approuver » est fermé à l'agent connecté quand il a lui-même demandé une
  // correction qui fait payer davantage à la famille, sauf au super validateur
  // sur l'un des trois types que le serveur lui ouvre.
  // « Refuser » reste ouvert. Un refus du serveur ferme le bouton pour tous.
  const approbationInterdite = (request) => {
    if (!request || request.statut !== 'en_attente') return false;
    if (approbationsRefusees.has(Number(request.id))) return true;
    const ouvertAuSuperValidateur = estSuperValidateur
      && TYPES_AUTO_VALIDATION_HAUSSE.includes(request.type_demande);
    return estMaCorrectionEnHausse(request) && !ouvertAuSuperValidateur;
  };

  // Le super validateur approuve sa propre correction en hausse : le bouton
  // reste possible, ses conditions sont écrites à côté et le code est demandé.
  const autoValidationHausse = (request) => (
    estSuperValidateur && estMaCorrectionEnHausse(request) && !approbationInterdite(request)
  );
  // Dans la fenêtre de décision : ses conditions, et la justification telle que
  // le serveur la mesurera (le motif de la demande).
  const hausseAutoValidee = Boolean(specialDecisionModal)
    && specialDecisionModal.decision === 'approve'
    && autoValidationHausse(specialDecisionModal.request);
  const longueurJustification = String(specialDecisionModal?.request?.motif || '').trim().length;

  const handleProcessSpecialRequest = async () => {
    if (!specialDecisionModal?.request) return;
    if (specialDecisionModal.decision === 'reject' && !specialDecisionComment.trim()) {
      showToast('Le motif du refus est obligatoire.', 'error');
      return;
    }
    if (specialDecisionModal.decision === 'approve' && approbationInterdite(specialDecisionModal.request)) {
      showToast(PHRASE_AUTO_VALIDATION_HAUSSE, 'error', { long: true });
      return;
    }

    const autoValidation = estAutoValidationPrivilegiee(specialDecisionModal)
      || (specialDecisionModal.decision === 'approve' && autoValidationHausse(specialDecisionModal.request));
    if (autoValidation && !/^\d{6}$/.test(codeDoubleAuth.trim())) {
      showToast('Saisissez le code à 6 chiffres de votre application de double authentification.', 'error');
      return;
    }

    setIsProcessing(true);
    try {
      const response = await api.support.processSpecialRequest(specialDecisionModal.request.id, {
        decision: specialDecisionModal.decision,
        commentaire: specialDecisionComment.trim(),
        ...(autoValidation ? { code_totp: codeDoubleAuth.trim() } : {})
      });

      if (response.success) {
        const decision = specialDecisionModal.decision;
        // Même règle que pour les demandes d'accès : une fenêtre à la fois.
        setSpecialDecisionModal(null);
        await attendre(DELAI_FERMETURE);
        setSelectedSpecialRequest(null);
        await attendre(DELAI_FERMETURE);
        // ⚠️ LE MESSAGE LE PLUS IMPORTANT DU LOT, et le plus facile à manquer.
        // Quand le validateur approuve, la radiation ou la restauration est
        // RÉELLEMENT EXÉCUTÉE — mais sur des données qu'il n'a pas sous les yeux :
        // rien ne bouge sur son écran. Ce message est donc sa seule confirmation
        // qu'un élève vient d'être radié pour de bon, et il tenait 3 secondes.
        // Le type avant les options : passées en deuxième argument, celles-ci
        // devenaient le type (toast « info » de 3 secondes, durée ignorée).
        showToast(decision === 'approve' ? 'Demande spéciale approuvée.' : 'Demande spéciale refusée.', 'success', { duration: 8000 });
        await Promise.all([loadSpecialRequests(), loadSpecialHistory()]);
      } else if (response.code === CODE_AUTO_VALIDATION_HAUSSE) {
        // La demande reste en attente : la fenêtre reste ouverte, « Confirmer »
        // se grise et la phrase s'y lit. Le toast long ne part pas seul.
        const id = Number(specialDecisionModal.request.id);
        setApprobationsRefusees((avant) => new Set(avant).add(id));
        showToast(response.message || PHRASE_AUTO_VALIDATION_HAUSSE, 'error', { long: true });
      } else {
        showToast(response.message || response.error || 'Traitement impossible.', 'error');
      }
    } catch (error) {
      showToast(error.message || 'Erreur lors du traitement.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const renderAccessRequests = () => (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-col gap-4 border-b border-gray-200 p-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Demandes d'accès</h2>
          <p className="text-sm text-gray-500">Création de comptes et réinitialisations en attente.</p>
        </div>
        <SearchBox value={searchTerm} onChange={setSearchTerm} placeholder="Rechercher une demande..." />
      </div>

      {isLoading ? (
        <LoadingState />
      ) : erreurChargement ? (
        <div className="p-5">
          <ErrorState message={erreurChargement} onRetry={loadInitialData} />
        </div>
      ) : filteredAccessRequests.length === 0 ? (
        <div className="p-5">
          <EmptyState icon={UserCheck} title="Aucune demande en attente" text="Les demandes d'accès apparaîtront ici dès qu'un utilisateur en créera une." />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-5 py-3">Employé concerné</th>
                <th className="px-5 py-3">Type</th>
                <th className="px-5 py-3">Demandeur</th>
                <th className="px-5 py-3">Date</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredAccessRequests.map((request) => (
                <tr key={request.id} className="hover:bg-gray-50">
                  <td className="px-5 py-4">
                    <div className="font-bold text-gray-900">{getName(request.prenom, request.nom)}</div>
                    <div className="text-xs text-gray-500">{request.email || '-'}</div>
                  </td>
                  <td className="px-5 py-4">
                    <span className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold text-[#246990]">
                      {typeLabel(request.type_demande)}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="font-semibold text-gray-800">{getName(request.demandeur_prenom, request.demandeur_nom)}</div>
                    <div className="text-xs text-gray-500">{request.demandeur_role || '-'}</div>
                  </td>
                  <td className="px-5 py-4 text-gray-600">{formatDateTime(request.date_demande)}</td>
                  <td className="px-5 py-4">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setSelectedAccessRequest(request)}
                        className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-100"
                      >
                        <Eye size={14} /> Détails
                      </button>
                      <button
                        type="button"
                        onClick={() => openProcessModal(request, 'approve')}
                        className="inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-3 py-1.5 text-xs font-semibold text-green-700 hover:bg-green-100"
                      >
                        <Check size={14} /> Approuver
                      </button>
                      <button
                        type="button"
                        onClick={() => openProcessModal(request, 'reject')}
                        className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100"
                      >
                        <X size={14} /> Refuser
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderSpecialRequests = () => (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_1.2fr]">
        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-[#246990]/10 p-3 text-[#246990]">
              <Settings size={22} />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-bold text-gray-900">Groupe validateur</h2>
              <p className="mt-1 text-sm text-gray-500">
                Les demandes sensibles sont envoyées au groupe choisi, avec notification aux agents concernés.
              </p>
              <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                <Select value={validatorGroupId} onValueChange={setValidatorGroupId}>
                  <SelectTrigger className="h-11 flex-1 rounded-lg border-gray-200">
                    <SelectValue placeholder="Sélectionner un groupe" />
                  </SelectTrigger>
                  <SelectContent>
                    {securityGroups.map((group) => (
                      <SelectItem key={group.id} value={String(group.id)}>
                        {group.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button
                  type="button"
                  onClick={handleSaveSpecialConfig}
                  disabled={isSavingConfig}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-[#246990] px-4 text-sm font-semibold text-white hover:bg-[#1f5b7d] disabled:opacity-60"
                >
                  {isSavingConfig ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                  Enregistrer
                </button>
              </div>
              <p className="mt-3 text-xs text-gray-500">
                Groupe actuel : <span className="font-semibold text-gray-800">{stats.validator}</span>
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-amber-200 bg-amber-50 p-5">
          <div className="flex items-start gap-3">
            <AlertCircle size={22} className="mt-1 text-amber-600" />
            <div>
              <h3 className="font-bold text-amber-900">Demandes spéciales</h3>
              <p className="mt-1 text-sm leading-6 text-amber-800">
                Cette file servira aux validations sensibles : modification de réductions déjà appliquées,
                création de famille depuis des élèves déjà inscrits, ou tout changement qui doit être assumé par la direction.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-4 border-b border-gray-200 p-5 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">À traiter</h2>
            <p className="text-sm text-gray-500">Les décisions prises ici valident et appliquent les demandes sensibles via le module métier concerné quand elles sont approuvées.</p>
          </div>
          <SearchBox value={searchTerm} onChange={setSearchTerm} placeholder="Rechercher une demande spéciale..." />
        </div>

        {isLoading ? (
          <LoadingState />
        ) : erreurChargement ? (
          <div className="p-5">
            <ErrorState message={erreurChargement} onRetry={loadInitialData} />
          </div>
        ) : filteredSpecialRequests.length === 0 ? (
          <div className="p-5">
            <EmptyState icon={ShieldCheck} title="Aucune demande spéciale" text="Les demandes sensibles apparaîtront ici dès que les modules métier les enverront." />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-5 py-3">Objet</th>
                  <th className="px-5 py-3">Type</th>
                  <th className="px-5 py-3">Demandeur</th>
                  <th className="px-5 py-3">Groupe</th>
                  <th className="px-5 py-3">Date</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredSpecialRequests.map((request) => {
                  const interdite = approbationInterdite(request);
                  const autoHausse = autoValidationHausse(request);
                  const mentionApprouver = interdite
                    ? `approbation-interdite-${request.id}`
                    : autoHausse ? `auto-validation-hausse-${request.id}` : undefined;
                  return (
                    <tr key={request.id} className="hover:bg-gray-50">
                      <td className="px-5 py-4">
                        <div className="font-bold text-gray-900">{request.titre}</div>
                        <div className="max-w-md truncate text-xs text-gray-500">{request.description || request.motif || '-'}</div>
                        {interdite && (
                          <p id={`approbation-interdite-${request.id}`} className="mt-1 max-w-md text-xs font-medium text-amber-800">
                            {PHRASE_AUTO_VALIDATION_HAUSSE}
                          </p>
                        )}
                        {autoHausse && (
                          <p id={`auto-validation-hausse-${request.id}`} className="mt-1 max-w-md text-xs font-medium text-amber-800">
                            {MENTION_SUPER_VALIDATEUR_HAUSSE}
                          </p>
                        )}
                      </td>
                      <td className="px-5 py-4">
                        <span className="rounded-full border border-violet-200 bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700">
                          {typeLabel(request.type_demande)}
                        </span>
                      </td>
                      <td className="px-5 py-4">{getName(request.demandeur_prenom, request.demandeur_nom)}</td>
                      <td className="px-5 py-4 text-gray-600">{request.groupe_validateur_nom || '-'}</td>
                      <td className="px-5 py-4 text-gray-600">{formatDateTime(request.date_demande)}</td>
                      <td className="px-5 py-4">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => setSelectedSpecialRequest(request)}
                            className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-100"
                          >
                            <Eye size={14} /> Voir
                          </button>
                          <button
                            type="button"
                            onClick={() => openSpecialDecisionModal(request, 'approve')}
                            disabled={interdite}
                            aria-describedby={mentionApprouver}
                            className="inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-3 py-1.5 text-xs font-semibold text-green-700 hover:bg-green-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-400 disabled:hover:bg-gray-50"
                          >
                            <Check size={14} /> Approuver
                          </button>
                          <button
                            type="button"
                            onClick={() => openSpecialDecisionModal(request, 'reject')}
                            className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100"
                          >
                            <X size={14} /> Refuser
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderHistory = () => (
    <div className="space-y-5">
      <HistorySection
        title="Historique des demandes d'accès"
        subtitle="Créations de compte et réinitialisations déjà traitées."
        loading={isLoadingHistory}
        erreur={erreurChargement}
        onRetry={loadInitialData}
        emptyTitle="Aucun historique d'accès"
        rows={historiqueRequests}
        renderRow={(item) => (
          <div key={`access-${item.id}`} className="grid grid-cols-1 gap-3 border-b border-gray-100 px-5 py-4 last:border-b-0 lg:grid-cols-[1.1fr_1fr_1fr_160px]">
            <div>
              <div className="font-bold text-gray-900">{getName(item.employee_prenom, item.employee_nom)}</div>
              <div className="text-xs text-gray-500">{item.employee_email || '-'}</div>
            </div>
            <div className="text-sm text-gray-600">{typeLabel(item.type_demande)}</div>
            <div className="text-sm text-gray-600">Traité par {getName(item.traite_par_prenom, item.traite_par_nom)}</div>
            <div className="text-right">
              <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${statusClasses(item.action)}`}>
                {normalizeStatus(item.action)}
              </span>
              <div className="mt-1 text-xs text-gray-500">{item.date_action_formattee || formatDateTime(item.date_action)}</div>
            </div>
          </div>
        )}
      />

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          disabled={!historyPagination.hasPrev || isLoadingHistory}
          onClick={() => loadHistorique(historyPagination.currentPage - 1)}
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-600 disabled:opacity-40"
        >
          Précédent
        </button>
        <span className="text-sm text-gray-500">
          Page {historyPagination.currentPage} / {historyPagination.totalPages}
        </span>
        <button
          type="button"
          disabled={!historyPagination.hasNext || isLoadingHistory}
          onClick={() => loadHistorique(historyPagination.currentPage + 1)}
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-600 disabled:opacity-40"
        >
          Suivant
        </button>
      </div>

      {circuitValidation && (
        <HistorySection
          title="Historique des demandes spéciales"
          subtitle="Décisions sensibles validées ou refusées."
          loading={false}
          erreur={erreurChargement}
          onRetry={loadInitialData}
          emptyTitle="Aucune demande spéciale traitée"
          rows={specialHistory}
          renderRow={(item) => (
            <div key={`special-${item.id}`} className="grid grid-cols-1 gap-3 border-b border-gray-100 px-5 py-4 last:border-b-0 lg:grid-cols-[1.4fr_1fr_1fr_160px]">
              <div>
                <div className="font-bold text-gray-900">{item.titre}</div>
                <div className="text-xs text-gray-500">{typeLabel(item.type_demande)}</div>
              </div>
              <div className="text-sm text-gray-600">Demandé par {getName(item.demandeur_prenom, item.demandeur_nom)}</div>
              <div className="text-sm text-gray-600">Décidé par {getName(item.validateur_prenom, item.validateur_nom)}</div>
              <div className="text-right">
                <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${statusClasses(item.statut)}`}>
                  {normalizeStatus(item.statut)}
                </span>
                <div className="mt-1 text-xs text-gray-500">{formatDateTime(item.date_decision || item.updated_at)}</div>
              </div>
            </div>
          )}
        />
      )}
    </div>
  );

  if (chargementDroits) {
    return <LoadingState />;
  }

  if (!peutConsulter) {
    return <SupportFerme />;
  }

  return (
    <div className="space-y-6">



      <div className={`grid grid-cols-1 gap-4 md:grid-cols-2 ${circuitValidation ? 'xl:grid-cols-4' : ''}`}>
        <StatCard title="Demandes d'accès" value={stats.accessPending} subtitle="En attente de traitement" icon={KeyRound} color="bg-[#246990]" />
        {circuitValidation && (
          <StatCard title="Demandes spéciales" value={stats.specialPending} subtitle="Validations sensibles" icon={ShieldCheck} color="bg-[#c66d59]" />
        )}
        <StatCard title="Historique" value={stats.historyTotal} subtitle="Décisions enregistrées" icon={History} color="bg-[#3f9c9a]" />
        {circuitValidation && (
          <StatCard title="Groupe validateur" value={stats.validator} subtitle="Routage des demandes sensibles" icon={Settings} color="bg-[#243244]" />
        )}
      </div>

      <div className={`grid grid-cols-1 gap-2 rounded-lg bg-gray-100 p-1 ${circuitValidation ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
        <TabButton
          active={activeTab === 'access_requests'}
          icon={KeyRound}
          label="Demandes d'accès"
          count={stats.accessPending}
          onClick={() => handleTabChange('access_requests')}
        />
        {circuitValidation && (
          <TabButton
            active={activeTab === 'special_requests'}
            icon={ShieldCheck}
            label="Demandes spéciales"
            count={stats.specialPending}
            onClick={() => handleTabChange('special_requests')}
          />
        )}
        <TabButton
          active={activeTab === 'history'}
          icon={History}
          label="Historique"
          count={stats.historyTotal}
          onClick={() => handleTabChange('history')}
        />
      </div>

      {activeTab === 'access_requests' && renderAccessRequests()}
      {activeTab === 'special_requests' && circuitValidation && renderSpecialRequests()}
      {activeTab === 'history' && renderHistory()}

      {/*
        ⚠️ L'ARBITRAGE, ET NON `<Toast>` EN DUR.
        Cet écran rendait le toast court directement : il ne pouvait donc PAS
        honorer une option `long`, le composant court n'ayant ni `options`, ni
        `title`, ni `long`. Et le seuil automatique des 130 caractères ne le
        sauvait pas — les messages de ce module mesurent entre 44 et 104
        caractères, donc tous condamnés aux 3 secondes du toast court.
        Même motif que FamillesNombreuses et EmployeesList, déjà en place.
      */}
      {toast.show && (
        shouldUseLongToast(toast.message, toast.type, toast) ? (
          <LongToast
            show
            title={toast.title}
            message={toast.message}
            type={toast.type}
            onClose={() => setToast({ ...toast, show: false })}
          />
        ) : (
          <Toast
            duration={toast.duration}
            message={toast.message}
            type={toast.type}
            onClose={() => setToast({ ...toast, show: false })}
          />
        )
      )}

      {selectedAccessRequest && (
        <ModalShell
          title="Détail de la demande d'accès"
          description="Ce que le demandeur a saisi, avant de trancher."
          icon={KeyRound}
          onClose={() => setSelectedAccessRequest(null)}
          footer={
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => openProcessModal(selectedAccessRequest, 'reject')}
                className={`${BOUTON_PIED} !border-red-200 bg-red-50 text-red-700 hover:bg-red-100`}
              >
                <XCircle size={16} /> Refuser
              </button>
              <button
                type="button"
                onClick={() => openProcessModal(selectedAccessRequest, 'approve')}
                className={`${BOUTON_PIED} bg-[#246990] text-white hover:bg-[#1f5b7d]`}
              >
                <CheckCircle size={16} /> Approuver
              </button>
            </div>
          }
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <InfoCard label="Employé concerné" value={getName(selectedAccessRequest.prenom, selectedAccessRequest.nom)} detail={selectedAccessRequest.email} />
            <InfoCard label="Type de demande" value={typeLabel(selectedAccessRequest.type_demande)} />
            <InfoCard label="Demandeur" value={getName(selectedAccessRequest.demandeur_prenom, selectedAccessRequest.demandeur_nom)} detail={selectedAccessRequest.demandeur_role} />
            <InfoCard label="Date de demande" value={formatDateTime(selectedAccessRequest.date_demande)} />
          </div>
        </ModalShell>
      )}

      {processModal && (
        <ModalShell
          title={processModal.action === 'approve' ? 'Approuver la demande' : 'Refuser la demande'}
          description={processModal.action === 'approve'
            ? "Le compte sera activé et les identifiants générés."
            : "Le demandeur verra le motif que vous écrivez ici."}
          icon={processModal.action === 'approve' ? CheckCircle : XCircle}
          onClose={() => setProcessModal(null)}
          footer={
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setProcessModal(null)}
                className={`${BOUTON_PIED} !border-gray-200 bg-white text-gray-600 hover:bg-gray-50`}
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleProcessAccessRequest}
                disabled={isProcessing}
                className={`${BOUTON_PIED} text-white ${processModal.action === 'approve' ? 'bg-[#246990] hover:bg-[#1f5b7d]' : 'bg-[#c66d59] hover:bg-[#b75f4c]'
                  }`}
              >
                {isProcessing && <Loader2 size={16} className="animate-spin" />}
                Confirmer
              </button>
            </div>
          }
        >
          <p className="text-sm text-gray-600">
            Vous êtes sur le point de {processModal.action === 'approve' ? 'approuver' : 'refuser'} la demande de{' '}
            <span className="font-semibold text-gray-900">{getName(processModal.request.prenom, processModal.request.nom)}</span>.
          </p>
          <label className="mt-5 block text-sm font-semibold text-gray-700">
            Note ou motif {processModal.action === 'reject' ? '(obligatoire)' : '(optionnel)'}
          </label>
          <textarea
            value={processNotes}
            onChange={(event) => setProcessNotes(event.target.value)}
            rows={4}
            className="mt-2 w-full rounded-lg border border-gray-200 p-3 text-sm outline-none focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
            placeholder="Ajouter une note de traitement..."
          />
        </ModalShell>
      )}

      {selectedSpecialRequest && (
        <ModalShell
          title="Détail de la demande spéciale"
          description="Ce qui changera si vous approuvez."
          onClose={() => setSelectedSpecialRequest(null)}
          footer={
            selectedSpecialRequest.statut === 'en_attente' && (
              <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
                {approbationInterdite(selectedSpecialRequest) && (
                  <p
                    id="approbation-interdite-detail"
                    className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 sm:mr-auto sm:max-w-xl"
                  >
                    {PHRASE_AUTO_VALIDATION_HAUSSE}
                  </p>
                )}
                {autoValidationHausse(selectedSpecialRequest) && (
                  <p
                    id="auto-validation-hausse-detail"
                    className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 sm:mr-auto sm:max-w-xl"
                  >
                    {MENTION_SUPER_VALIDATEUR_HAUSSE}
                  </p>
                )}
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => openSpecialDecisionModal(selectedSpecialRequest, 'reject')}
                    className={`${BOUTON_PIED} !border-red-200 bg-red-50 text-red-700 hover:bg-red-100`}
                  >
                    <XCircle size={16} /> Refuser
                  </button>
                  <button
                    type="button"
                    onClick={() => openSpecialDecisionModal(selectedSpecialRequest, 'approve')}
                    disabled={approbationInterdite(selectedSpecialRequest)}
                    aria-describedby={approbationInterdite(selectedSpecialRequest)
                      ? 'approbation-interdite-detail'
                      : autoValidationHausse(selectedSpecialRequest) ? 'auto-validation-hausse-detail' : undefined}
                    className={`${BOUTON_PIED} bg-[#246990] text-white hover:bg-[#1f5b7d] disabled:cursor-not-allowed disabled:hover:bg-[#246990]`}
                  >
                    <CheckCircle size={16} /> Approuver
                  </button>
                </div>
              </div>
            )
          }
        >
          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <InfoCard label="Objet" value={selectedSpecialRequest.titre} />
              <InfoCard label="Type" value={typeLabel(selectedSpecialRequest.type_demande)} />
              <InfoCard label="Statut" value={normalizeStatus(selectedSpecialRequest.statut)} />
              <InfoCard label="Demandeur" value={getName(selectedSpecialRequest.demandeur_prenom, selectedSpecialRequest.demandeur_nom)} />
              <InfoCard label="Groupe validateur" value={selectedSpecialRequest.groupe_validateur_nom || '-'} />
              <InfoCard label="Demandé le" value={formatDateTime(selectedSpecialRequest.date_demande)} />
            </div>
            {(selectedSpecialRequest.description || selectedSpecialRequest.motif) && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <div className="text-xs font-bold uppercase tracking-wide text-gray-500">Motif</div>
                <p className="mt-2 text-sm leading-6 text-gray-700">
                  {selectedSpecialRequest.description || selectedSpecialRequest.motif}
                </p>
              </div>
            )}
            {selectedSpecialRequest.type_demande === 'modification_salaire_employe' ? (
              renderSalaryModificationDetails(selectedSpecialRequest)
            ) : selectedSpecialRequest.type_demande === 'modification_infos_employe' ? (
              renderEmployeeProfileModificationDetails(selectedSpecialRequest)
            ) : aUnDescripteur(selectedSpecialRequest.type_demande) ? (
              /* Neuf types de demandes tombaient dans le rendu JSON ci-dessous.
                 Une demande se lit des jours apres avoir ete ecrite, souvent par
                 quelqu'un qui n'a pas eu la conversation qui l'a precedee : elle
                 doit se comprendre sans decodage. */
              <DetailsDemandeStructuree request={selectedSpecialRequest} />
            ) : (
              <>
                <div>
                  <h4 className="text-sm font-bold uppercase tracking-wide text-gray-600">Situation actuelle</h4>
                  <div className="mt-2">{renderPayload(selectedSpecialRequest.payload_avant)}</div>
                </div>
                <div>
                  <h4 className="text-sm font-bold uppercase tracking-wide text-gray-600">Changement demandé</h4>
                  <div className="mt-2">{renderPayload(selectedSpecialRequest.payload_demande)}</div>
                </div>
              </>
            )}
          </div>
        </ModalShell>
      )}

      {specialDecisionModal && (
        <ModalShell
          title={specialDecisionModal.decision === 'approve' ? 'Approuver la demande spéciale' : 'Refuser la demande spéciale'}
          description={specialDecisionModal.decision === 'approve'
            ? "Le module métier concerné appliquera le changement immédiatement."
            : "Le demandeur verra le motif que vous écrivez ici."}
          icon={specialDecisionModal.decision === 'approve' ? CheckCircle : XCircle}
          onClose={() => setSpecialDecisionModal(null)}
          footer={
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setSpecialDecisionModal(null)}
                className={`${BOUTON_PIED} !border-gray-200 bg-white text-gray-600 hover:bg-gray-50`}
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleProcessSpecialRequest}
                disabled={isProcessing || (specialDecisionModal.decision === 'approve' && approbationInterdite(specialDecisionModal.request))}
                className={`${BOUTON_PIED} text-white disabled:cursor-not-allowed ${specialDecisionModal.decision === 'approve' ? 'bg-[#246990] hover:bg-[#1f5b7d]' : 'bg-[#c66d59] hover:bg-[#b75f4c]'
                  }`}
              >
                {isProcessing && <Loader2 size={16} className="animate-spin" />}
                Confirmer
              </button>
            </div>
          }
        >
          <p className="text-sm text-gray-600">
            Demande : <span className="font-semibold text-gray-900">{specialDecisionModal.request.titre}</span>
          </p>
          {specialDecisionModal.decision === 'approve' && approbationInterdite(specialDecisionModal.request) && (
            <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {PHRASE_AUTO_VALIDATION_HAUSSE}
            </p>
          )}
          <label className="mt-5 block text-sm font-semibold text-gray-700">
            Commentaire {specialDecisionModal.decision === 'reject' ? '(obligatoire)' : '(optionnel)'}
          </label>
          <textarea
            value={specialDecisionComment}
            onChange={(event) => setSpecialDecisionComment(event.target.value)}
            rows={4}
            className="mt-2 w-full rounded-lg border border-gray-200 p-3 text-sm outline-none focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
            placeholder="Ajouter un commentaire de décision..."
          />
          {(estAutoValidationPrivilegiee(specialDecisionModal) || hausseAutoValidee) && (
            <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-4">
              {hausseAutoValidee ? (
                <>
                  <p className="text-sm font-semibold text-amber-900">
                    Vous validez votre propre correction, qui fait payer davantage à la famille
                  </p>
                  <p className="mt-1 text-sm text-amber-800">Réservé au super validateur, à deux conditions :</p>
                  <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm text-amber-800">
                    <li>
                      la justification de la demande compte {LONGUEUR_JUSTIFICATION_AUTO} caractères au moins - elle en
                      compte {longueurJustification}
                      {longueurJustification < LONGUEUR_JUSTIFICATION_AUTO
                        ? ' : refusez la demande et refaites-la avec une justification complète'
                        : ''} ;
                    </li>
                    <li>votre code de double authentification, ci-dessous.</li>
                  </ul>
                  <p className="mt-1 text-sm text-amber-800">
                    L'approbation sera marquée « auto-validée » au journal des actions privilégiées et signalée à la direction.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-semibold text-amber-900">Vous validez votre propre demande</p>
                  <p className="mt-1 text-sm text-amber-800">
                    Réservé au super validateur. La validation sera marquée « auto-validée » au journal et signalée à la direction.
                  </p>
                </>
              )}
              <label className="mt-3 block text-sm font-semibold text-gray-700">Code de double authentification</label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={codeDoubleAuth}
                onChange={(event) => setCodeDoubleAuth(event.target.value.replace(/\D/g, ''))}
                className="mt-2 h-9 w-40 rounded-lg border border-gray-200 bg-white px-3 text-center text-sm tracking-[0.3em] outline-none focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                placeholder="000000"
              />
            </div>
          )}
        </ModalShell>
      )}

      {showCredentials && (
        <CredentialsModal
          credentials={credentials}
          typeDemande={typeDemande}
          onClose={() => {
            setShowCredentials(false);
            setCredentials(null);
          }}
        />
      )}
    </div>
  );
};

const SearchBox = ({ value, onChange, placeholder }) => (
  <div className="relative w-full lg:max-w-md">
    <Search size={18} className="absolute left-3 top-1/2 -trangray-y-1/2 text-gray-400" />
    <input
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      className="h-11 w-full rounded-lg border border-gray-200 bg-white pl-10 pr-3 text-sm outline-none focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
    />
  </div>
);

const LoadingState = () => (
  <div className="flex min-h-[220px] items-center justify-center">
    <Loader2 size={28} className="animate-spin text-[#246990]" />
  </div>
);

// Le pendant de `EmptyState`, et il ne lui ressemble pas : bordure pleine,
// teinte d'alerte, un bouton pour reessayer. « Il n'y a rien » et « je n'ai
// pas pu regarder » sont deux phrases differentes.
const ErrorState = ({ message, onRetry }) => (
  <div className="flex min-h-[220px] flex-col items-center justify-center rounded-lg border border-red-200 bg-red-50 p-8 text-center">
    <AlertCircle size={34} className="text-red-400" />
    <h3 className="mt-3 text-base font-semibold text-red-800">Lecture impossible</h3>
    <p className="mt-1 max-w-md text-sm text-red-700">{message}</p>
    <p className="mt-2 max-w-md text-xs text-red-600">
      Cette liste peut contenir des demandes en attente : ne la considérez pas comme vide.
    </p>
    {onRetry && (
      <button
        type="button"
        onClick={onRetry}
        className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3.5 py-2 text-xs font-semibold text-red-700 transition-all hover:bg-red-50"
      >
        <RefreshCw size={13} />
        Réessayer
      </button>
    )}
  </div>
);

// Entré par la porte ADMINISTRATION (ou par Outils administratifs > Accès &
// sécurité > Centre de support),
// sans le droit de lire le support. Même présentation que la double
// authentification fermée (AccessManagement) : un cadenas et la raison.
const SupportFerme = () => (
  <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-6">
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-200 text-gray-600">
      <Lock size={18} />
    </div>
    <div className="min-w-0 text-sm text-gray-700">
      <p className="font-semibold text-gray-900">Le Centre de support ne vous est pas ouvert</p>
      <p className="mt-1">
        Les demandes d'accès et les demandes spéciales se consultent avec le droit de consultation du
        Centre de support (ADMINISTRATION.SUPPORT.VIEW), que votre compte ne détient pas.
      </p>
      <p className="mt-1 text-gray-500">
        Votre administrateur l'accorde depuis Outils administratifs - Accès &amp; sécurité - Rôles &amp; Permissions.
      </p>
    </div>
  </div>
);

const InfoCard = ({ label, value, detail }) => (
  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
    <div className="text-[11px] font-bold uppercase tracking-wide text-gray-500">{label}</div>
    <div className="mt-2 break-words text-sm font-bold text-gray-900">{value || '-'}</div>
    {detail && <div className="mt-1 break-words text-xs text-gray-500">{detail}</div>}
  </div>
);

const HistorySection = ({ title, subtitle, loading, rows, renderRow, emptyTitle, erreur, onRetry }) => (
  <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
    <div className="border-b border-gray-200 p-5">
      <h2 className="text-lg font-bold text-gray-900">{title}</h2>
      <p className="text-sm text-gray-500">{subtitle}</p>
    </div>
    {loading ? (
      <LoadingState />
    ) : erreur ? (
      <div className="p-5"><ErrorState message={erreur} onRetry={onRetry} /></div>
    ) : rows.length === 0 ? (
      <div className="p-5">
        <EmptyState icon={Clock} title={emptyTitle} text="Les décisions traitées apparaîtront ici." />
      </div>
    ) : (
      <div>{rows.map(renderRow)}</div>
    )}
  </div>
);

export default Support;
